/**
 * Dialog de criação/edição de Tipo de Documento
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-09
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { DocumentType, DocumentTypeInput } from '@/hooks/useDocumentTypes';

/** Categorias de documento */
const DOCUMENT_CATEGORIES = [
  { value: 'geral', label: 'Geral' },
  { value: 'identificacao', label: 'Identificação' },
  { value: 'endereco', label: 'Endereço' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'outro', label: 'Outro' },
];

interface DocumentTypeFormDialogProps {
  open: boolean;
  onClose: () => void;
  documentType?: DocumentType | null;
  onSave: (input: DocumentTypeInput & { id?: string }) => void;
  isSaving?: boolean;
}

export function DocumentTypeFormDialog({
  open,
  onClose,
  documentType,
  onSave,
  isSaving = false,
}: DocumentTypeFormDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('geral');
  const [isActive, setIsActive] = useState(true);

  // Preencher com dados ao editar
  useEffect(() => {
    if (documentType) {
      setName(documentType.name);
      setDescription(documentType.description || '');
      setCategory(documentType.category || 'geral');
      setIsActive(documentType.is_active);
    } else {
      setName('');
      setDescription('');
      setCategory('geral');
      setIsActive(true);
    }
  }, [documentType, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSave({
      ...(documentType ? { id: documentType.id } : {}),
      name: name.trim(),
      description: description.trim() || undefined,
      category,
      is_active: isActive,
    });
  };

  const isEditing = !!documentType;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Tipo de Documento' : 'Novo Tipo de Documento'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Atualize as informações do tipo de documento.'
              : 'Crie um novo tipo de documento para usar em contratos.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="doc-type-name">Nome *</Label>
            <Input
              id="doc-type-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: RG, CNH, Comprovante de Endereço"
              required
            />
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="doc-type-desc">Descrição</Label>
            <Textarea
              id="doc-type-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva quando este documento é necessário..."
              rows={2}
            />
          </div>

          {/* Categoria */}
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Ativo */}
          <div className="flex items-center justify-between">
            <Label htmlFor="doc-type-active">Ativo</Label>
            <Switch
              id="doc-type-active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!name.trim() || isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
